import { __assign } from "tslib";
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useCallback, useEffect, useImperativeHandle, useRef } from "react";
import { useVirtual } from "react-virtual";
import OptionsList from "../internal/components/options-list";
import { useContainerQuery } from "../internal/hooks/container-queries";
import AutosuggestOption from "./autosuggest-option";
import { getOptionProps } from "./plain-list";
import styles from "./styles.css.js";
var VirtualList = function(_a) {
    var autosuggestItemsState = _a.autosuggestItemsState, handleLoadMore = _a.handleLoadMore, menuProps = _a.menuProps, enteredTextLabel = _a.enteredTextLabel, highlightedA11yProps = _a.highlightedA11yProps, hasDropdownStatus = _a.hasDropdownStatus, highlightText = _a.highlightText, listBottom = _a.listBottom, screenReaderContent = _a.screenReaderContent;
    var scrollRef = useRef(null);
    // update component, when it gets wider or narrower to reposition items
    var _b = useContainerQuery(function(rect) {
        return rect.width;
    }, []), width = _b[0], strutRef = _b[1];
    useImperativeHandle(strutRef, function() {
        return scrollRef.current;
    });
    var rowVirtualizer = useVirtual({
        size: autosuggestItemsState.items.length,
        parentRef: scrollRef,
        // estimateSize is a dependency of measurements memo. We update it to force full recalculation
        // when the height of any option could have changed:
        // 1: because the component got resized (width property got updated)
        // 2: becasue the option changed its content (highlightText property controls the highlight and the visibility of hidden tags)
        // eslint-disable-next-line react-hooks/exhaustive-deps
        estimateSize: useCallback(function() {
            return 31;
        }, [
            width,
            highlightText
        ]),
        overscan: 5
    });
    useEffect(function() {
        if (autosuggestItemsState.highlightType === "keyboard") {
            rowVirtualizer.scrollToIndex(autosuggestItemsState.highlightedIndex);
        }
    }, [
        autosuggestItemsState.highlightType,
        autosuggestItemsState.highlightedIndex,
        rowVirtualizer
    ]);
    return /*#__PURE__*/ React.createElement(OptionsList, __assign({}, menuProps, {
        onLoadMore: handleLoadMore,
        ref: scrollRef,
        open: true,
        // to prevent closing the list when clicking the scrollbar on IE11
        nativeAttributes: {
            unselectable: "on"
        }
    }), /*#__PURE__*/ React.createElement("div", {
        "aria-hidden": "true",
        key: "total-size",
        className: styles["layout-strut"],
        style: {
            height: rowVirtualizer.totalSize + (autosuggestItemsState.items.length === 1 ? 1 : 0)
        }
    }), rowVirtualizer.virtualItems.map(function(virtualRow) {
        var index = virtualRow.index, start = virtualRow.start, measureRef = virtualRow.measureRef;
        var item = autosuggestItemsState.items[index];
        var optionProps = getOptionProps(index, item, autosuggestItemsState.items, highlightedA11yProps, autosuggestItemsState.highlightedOption, hasDropdownStatus);
        return /*#__PURE__*/ React.createElement(AutosuggestOption, __assign({
            key: index,
            ref: measureRef,
            highlightText: highlightText,
            option: item,
            highlighted: item === autosuggestItemsState.highlightedOption,
            "data-mouse-target": index,
            enteredTextLabel: enteredTextLabel,
            virtualPosition: start + (index === 0 ? 1 : 0),
            screenReaderContent: screenReaderContent,
            ariaSetsize: autosuggestItemsState.items.length,
            ariaPosinset: index + 1,
            highlightType: autosuggestItemsState.highlightType
        }, optionProps));
    }), listBottom ? /*#__PURE__*/ React.createElement("li", {
        role: "option",
        className: styles["list-bottom"]
    }, listBottom) : null);
};
export default VirtualList; //# sourceMappingURL=virtual-list.js.map
